import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Search,
  X,
  Plus,
  Grid3X3,
  List,
  Users,
  ArrowLeft,
  Calendar,
  Database
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { matchesCandidateSearch } from '@/utils/searchUtils';
import { SkillsFilter } from '@/components/candidates/search/SkillsFilter';
import { FunctionFilter } from '@/components/candidates/search/FunctionFilter';
import { normalizeSkill } from '@/utils/skillNormalization';
import { AddFromQueryTab } from './AddFromQueryTab';

interface AddCandidateToJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCandidates: (candidateIds: string[]) => Promise<void>;
  jobId: string;
  isLoading?: boolean;
}

// Helper function to get current organization
const getCurrentOrganization = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: userOrgs } = await supabase
    .from('user_organization')
    .select('organization_uuid')
    .eq('user_uuid', user.id)
    .single();

  if (!userOrgs) throw new Error('User not associated with any organization');
  return userOrgs.organization_uuid;
};

// Hook to get all candidates for the organization
const useGetCandidates = () => {
  const [candidates, setCandidates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setIsLoading(true);
        const organizationId = await getCurrentOrganization();

        // Get all candidates for the organization
        const { data: allCandidates, error: candidatesError } = await supabase
          .from('candidates')
          .select(`
            id,
            name,
            email,
            phone,
            function,
            skills,
            status,
            created_at
          `)
          .eq('organization_uuid', organizationId)
          .order('name');

        if (candidatesError) throw candidatesError;

        setCandidates(allCandidates || []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCandidates();
  }, []);

  return { candidates, isLoading, error };
};

// Hook to get lists
const useGetLists = () => {
  const [lists, setLists] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    const fetchLists = async () => {
      try {
        setIsLoading(true);
        const organizationId = await getCurrentOrganization();

        const { data, error } = await supabase
          .from('lists_with_candidate_count')
          .select('*')
          .eq('organization_uuid', organizationId)
          .order('name');

        if (error) throw error;
        setLists(data || []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLists();
  }, []);

  return { lists, isLoading, error };
};

// Hook to get candidates in a list
const useGetCandidatesInList = (listId: string | null) => {
  const [candidates, setCandidates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    const fetchCandidates = async () => {
      if (!listId) {
        setCandidates([]);
        return;
      }

      try {
        setIsLoading(true);
        const organizationId = await getCurrentOrganization();

        // Get all candidates for the organization
        const { data: allCandidates, error: candidatesError } = await supabase
          .from('candidates')
          .select(`
            id,
            name,
            email,
            phone,
            function,
            skills,
            status,
            created_at
          `)
          .eq('organization_uuid', organizationId)
          .order('name');

        if (candidatesError) throw candidatesError;

        // Get candidates in the list
        const { data: listMembers, error: listError } = await supabase
          .from('list_members')
          .select('candidate_id')
          .eq('list_id', listId);

        if (listError) throw listError;

        const listCandidateIds = listMembers.map((lm: any) => lm.candidate_id);

        // Get candidates in the list
        const listCandidates = allCandidates.filter(
          (candidate: any) => listCandidateIds.includes(candidate.id)
        );

        setCandidates(listCandidates);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCandidates();
  }, [listId]);

  return { candidates, isLoading, error };
};

export const AddCandidateToJobModal: React.FC<AddCandidateToJobModalProps> = ({
  isOpen,
  onClose,
  onAddCandidates,
  jobId,
  isLoading: externalLoading = false
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'candidates' | 'lists' | 'queries'>('candidates');
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isAdding, setIsAdding] = useState(false);
  
  // Filter states
  const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
  const [skillsOperator, setSkillsOperator] = useState<"AND" | "OR">("AND");
  const [functionFilter, setFunctionFilter] = useState<string>("");

  // Data hooks
  const { candidates, isLoading: isLoadingCandidates, error: candidatesError } = useGetCandidates();
  const { lists, isLoading: isLoadingLists, error: listsError } = useGetLists();
  const { candidates: listCandidates, isLoading: isLoadingListCandidates, error: listCandidatesError } = useGetCandidatesInList(selectedListId);

  // Get all skills from candidates
  const allSkills = useMemo(() => {
    const skillsSet = new Set<string>();
    candidates.forEach((candidate: any) => {
      if (candidate.skills && Array.isArray(candidate.skills)) {
        candidate.skills.forEach((skill: string) => skillsSet.add(skill));
      }
    });
    return Array.from(skillsSet).sort();
  }, [candidates]);

  // Filter candidates based on search term, skills, and function filters
  const filteredCandidates = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const lowerFunctionFilter = functionFilter.toLowerCase();
    
    // Normalize the skills filter for comparison
    const normalizedSkillsFilter = skillsFilter.map(skill => normalizeSkill(skill));
    
    return candidates.filter((candidate: any) => {
      // Search term filter - universal search with word-order independence
      const matchesSearch = matchesCandidateSearch(candidate, searchTerm);
      
      // Skills filter - using normalized skills for proper comparison with AND/OR logic
      const matchesSkills = 
        normalizedSkillsFilter.length === 0 || 
        (candidate.skills && 
          (skillsOperator === "AND" 
            ? normalizedSkillsFilter.every(filterSkill => 
                candidate.skills.some(candidateSkill => 
                  normalizeSkill(candidateSkill) === filterSkill
                )
              )
            : normalizedSkillsFilter.some(filterSkill => 
                candidate.skills.some(candidateSkill => 
                  normalizeSkill(candidateSkill) === filterSkill
                )
              )
          )
        );
      
      // Function filter - case insensitive
      const matchesFunction = 
        functionFilter === "" || 
        (candidate.function && candidate.function.toLowerCase().includes(lowerFunctionFilter));
      
      return matchesSearch && matchesSkills && matchesFunction;
    });
  }, [candidates, searchTerm, skillsFilter, skillsOperator, functionFilter]);

  // Filter lists based on search term using universal search
  const filteredLists = useMemo(() => {
    return lists.filter((list: any) => {
      if (!searchTerm.trim()) return true;
      
      // Combine list fields for universal search
      const searchableText = [
        list.name,
        list.description || ''
      ].join(' ');
      
      const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
      return searchWords.every(word => 
        searchableText.toLowerCase().includes(word)
      );
    });
  }, [lists, searchTerm]);

  const handleCandidateToggle = (candidateId: string) => {
    setSelectedCandidates(prev => 
      prev.includes(candidateId) 
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    const currentCandidates = activeTab === 'candidates' ? filteredCandidates : listCandidates;
    const currentCandidateIds = currentCandidates.map((c: any) => c.id);
    
    if (selectedCandidates.length === currentCandidateIds.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(currentCandidateIds);
    }
  };

  const handleAddCandidates = async () => {
    if (selectedCandidates.length === 0) return;
    
    setIsAdding(true);
    try {
      await onAddCandidates(selectedCandidates);
      setSelectedCandidates([]);
      onClose();
    } catch (error) {
      console.error('Error adding candidates:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setSelectedCandidates([]);
    setSelectedListId(null);
    setSearchTerm('');
    setActiveTab('candidates');
    // Reset filters
    setSkillsFilter([]);
    setSkillsOperator("AND");
    setFunctionFilter("");
    onClose();
  };

  const handleListClick = (listId: string) => {
    setSelectedListId(listId);
    setSelectedCandidates([]);
    setSearchTerm('');
  };

  const handleBackToList = () => {
    setSelectedListId(null);
    setSelectedCandidates([]);
    setSearchTerm('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[95vw] w-[1600px] h-[95vh] flex flex-col [&>button]:hidden">
        <DialogHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold">
                {t("jobs.kanban.addCandidatesTitle", "Add Candidates to Job")}
              </DialogTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t("jobs.kanban.addCandidatesDescription", "Select candidates to add to this job position")}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b dark:border-gray-700">
          <button
            onClick={() => setActiveTab("candidates")}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "candidates"
                ? "border-blue-500 text-blue-500"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {t("jobs.kanban.tabs.candidates", "Candidates")}
          </button>
          <button
            onClick={() => setActiveTab("lists")}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === "lists"
                ? "border-blue-500 text-blue-500"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {t("jobs.kanban.tabs.lists", "Lists")}
          </button>
          <button
            onClick={() => setActiveTab("queries")}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === "queries"
                ? "border-blue-500 text-blue-500"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            <Database className="h-4 w-4" />
            {t("jobs.kanban.tabs.queries", "Queries")}
          </button>
        </div>

        {/* Content */}
        {activeTab === "candidates" ? (
          <div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-hidden">
            {/* Search and Filters */}
            <div className="px-4 py-2">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search Bar */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder={t("jobs.kanban.searchPlaceholder", "Search candidates by name, email, or function...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10 h-10"
                  />
                  {searchTerm && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      onClick={() => setSearchTerm('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Filters */}
                <div className="flex gap-2">
                  <SkillsFilter 
                    skillsFilter={skillsFilter} 
                    setSkillsFilter={setSkillsFilter}
                    skillsOperator={skillsOperator}
                    setSkillsOperator={setSkillsOperator}
                    allSkills={allSkills} 
                  />
                  
                  <FunctionFilter 
                    functionFilter={functionFilter} 
                    setFunctionFilter={setFunctionFilter} 
                  />
                </div>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex flex-wrap gap-2 items-center justify-between px-4">
              <div></div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
                <ToggleGroup 
                  type="single" 
                  value={viewMode} 
                  onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
                  className="h-8 bg-gray-50 dark:bg-gray-800 rounded-sm p-1"
                >
                  <ToggleGroupItem 
                    value="grid" 
                    className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem 
                    value="list" 
                    className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
                  >
                    <List className="h-4 w-4" />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            {/* Candidates List */}
            <div className="flex-1 overflow-y-auto border dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800/50 min-h-0">
              {isLoadingCandidates ? (
                <div className="p-8 text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading candidates...</p>
                </div>
              ) : candidatesError ? (
                <div className="p-8 text-center">
                  <p className="text-red-600">Error loading candidates: {candidatesError}</p>
                </div>
              ) : filteredCandidates.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {searchTerm ? t("jobs.kanban.noCandidatesFound", "No candidates found") : t("jobs.kanban.noCandidatesAvailable", "No candidates available")}
                  </h3>
                  <p className="text-gray-500">
                    {searchTerm 
                      ? t("jobs.kanban.tryDifferentSearch", "Try a different search term")
                      : t("jobs.kanban.allCandidatesAdded", "All candidates have already been added to this job")
                    }
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {filteredCandidates.map((candidate: any) => (
                    <div
                      key={candidate.id}
                      className={`relative bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedCandidates.includes(candidate.id)
                          ? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
                      }`}
                      onClick={() => handleCandidateToggle(candidate.id)}
                    >
                      {/* Selection Checkbox */}
                      <div className="absolute top-3 right-3">
                        <Checkbox
                          checked={selectedCandidates.includes(candidate.id)}
                          onChange={() => handleCandidateToggle(candidate.id)}
                          className="h-4 w-4"
                        />
                      </div>

                      {/* Candidate Info */}
                      <div className="flex items-start space-x-3 mb-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400">
                            {candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{candidate.name}</h3>
                          {candidate.function && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{candidate.function}</p>
                          )}
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-1 mb-3">
                        {candidate.email && (
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Search className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{candidate.email}</span>
                          </div>
                        )}
                        {candidate.phone && (
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Search className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{candidate.phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Skills */}
                      {candidate.skills && candidate.skills.length > 0 && (
                        <div className="mb-3">
                          <div className="flex flex-wrap gap-1">
                            {candidate.skills.slice(0, 2).map((skill: string, index: number) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.skills.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{candidate.skills.length - 2}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Status */}
                      {candidate.status && (
                        <div className="flex items-center">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              candidate.status.toLowerCase() === 'active' 
                                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' 
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            {candidate.status}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2 p-4">
                  {filteredCandidates.map((candidate: any) => (
                    <div
                      key={candidate.id}
                      className={`flex items-center space-x-4 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-sm cursor-pointer transition-all duration-200 hover:shadow-sm ${
                        selectedCandidates.includes(candidate.id)
                          ? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
                      }`}
                      onClick={() => handleCandidateToggle(candidate.id)}
                    >
                      <Checkbox
                        checked={selectedCandidates.includes(candidate.id)}
                        onChange={() => handleCandidateToggle(candidate.id)}
                        className="h-4 w-4"
                      />
                      
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 text-xs">
                          {candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{candidate.name}</h3>
                        {candidate.function && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.function}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                        {candidate.email && (
                          <div className="flex items-center">
                            <Search className="h-3 w-3 mr-1" />
                            <span className="truncate max-w-32">{candidate.email}</span>
                          </div>
                        )}
                        {candidate.phone && (
                          <div className="flex items-center">
                            <Search className="h-3 w-3 mr-1" />
                            <span>{candidate.phone}</span>
                          </div>
                        )}
                      </div>
                      
                      {candidate.skills && candidate.skills.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <div className="flex flex-wrap gap-1">
                            {candidate.skills.slice(0, 3).map((skill: string, index: number) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.skills.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{candidate.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {candidate.status && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            candidate.status.toLowerCase() === 'active' 
                              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' 
                              : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          {candidate.status}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === "lists" ? (
          <div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-hidden">
            {selectedListId ? (
              // Show candidates in selected list
              <>
                {/* Back Button */}
                <div className="flex items-center gap-4 px-4 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToList}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Lists
                  </Button>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {lists.find((list: any) => list.id === selectedListId)?.name} - Candidates
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {listCandidates.length} candidates available
                    </p>
                  </div>
                </div>

                {/* View Toggle */}
                <div className="flex items-center justify-end gap-2 px-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
                  <ToggleGroup 
                    type="single" 
                    value={viewMode} 
                    onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
                    className="h-8 bg-gray-50 dark:bg-gray-800 rounded-sm p-1"
                  >
                    <ToggleGroupItem 
                      value="grid" 
                      className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem 
                      value="list" 
                      className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
                    >
                      <List className="h-4 w-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                {/* Candidates List */}
                <div className="flex-1 overflow-y-auto border dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800/50 min-h-0">
                  {isLoadingListCandidates ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-500 dark:text-gray-400">Loading candidates...</p>
                    </div>
                  ) : listCandidates.length === 0 ? (
                    <div className="p-8 text-center">
                      <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        No candidates in this list
                      </h3>
                      <p className="text-gray-500">
                        This list doesn't contain any candidates
                      </p>
                    </div>
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                      {listCandidates.map((candidate: any) => (
                        <div
                          key={candidate.id}
                          className={`relative bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
                            selectedCandidates.includes(candidate.id)
                              ? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
                          }`}
                          onClick={() => handleCandidateToggle(candidate.id)}
                        >
                          {/* Selection Checkbox */}
                          <div className="absolute top-3 right-3">
                            <Checkbox
                              checked={selectedCandidates.includes(candidate.id)}
                              onChange={() => handleCandidateToggle(candidate.id)}
                              className="h-4 w-4"
                            />
                          </div>

                          {/* Candidate Info */}
                          <div className="flex items-start space-x-3 mb-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400">
                                {candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{candidate.name}</h3>
                              {candidate.function && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{candidate.function}</p>
                              )}
                            </div>
                          </div>

                          {/* Contact Info */}
                          <div className="space-y-1 mb-3">
                            {candidate.email && (
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Search className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{candidate.email}</span>
                              </div>
                            )}
                            {candidate.phone && (
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Search className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{candidate.phone}</span>
                              </div>
                            )}
                          </div>

                          {/* Skills */}
                          {candidate.skills && candidate.skills.length > 0 && (
                            <div className="mb-3">
                              <div className="flex flex-wrap gap-1">
                                {candidate.skills.slice(0, 2).map((skill: string, index: number) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {skill}
                                  </Badge>
                                ))}
                                {candidate.skills.length > 2 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{candidate.skills.length - 2}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Status */}
                          {candidate.status && (
                            <div className="flex items-center">
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  candidate.status.toLowerCase() === 'active' 
                                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' 
                                    : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                                }`}
                              >
                                {candidate.status}
                              </Badge>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2 p-4">
                      {listCandidates.map((candidate: any) => (
                        <div
                          key={candidate.id}
                          className={`flex items-center space-x-4 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-sm cursor-pointer transition-all duration-200 hover:shadow-sm ${
                            selectedCandidates.includes(candidate.id)
                              ? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
                          }`}
                          onClick={() => handleCandidateToggle(candidate.id)}
                        >
                          <Checkbox
                            checked={selectedCandidates.includes(candidate.id)}
                            onChange={() => handleCandidateToggle(candidate.id)}
                            className="h-4 w-4"
                          />
                          
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 text-xs">
                              {candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{candidate.name}</h3>
                            {candidate.function && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.function}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                            {candidate.email && (
                              <div className="flex items-center">
                                <Search className="h-3 w-3 mr-1" />
                                <span className="truncate max-w-32">{candidate.email}</span>
                              </div>
                            )}
                            {candidate.phone && (
                              <div className="flex items-center">
                                <Search className="h-3 w-3 mr-1" />
                                <span>{candidate.phone}</span>
                              </div>
                            )}
                          </div>
                          
                          {candidate.skills && candidate.skills.length > 0 && (
                            <div className="flex items-center space-x-2">
                              <div className="flex flex-wrap gap-1">
                                {candidate.skills.slice(0, 3).map((skill: string, index: number) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {skill}
                                  </Badge>
                                ))}
                                {candidate.skills.length > 3 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{candidate.skills.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {candidate.status && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                candidate.status.toLowerCase() === 'active' 
                                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' 
                                  : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              {candidate.status}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Show lists
              <>
                {/* Search Bar */}
                <div className="relative px-4 py-2">
                  <Search className="absolute left-7 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder={t("jobs.kanban.searchListsPlaceholder", "Search lists by name or description...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10 h-10"
                  />
                  {searchTerm && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-6 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      onClick={() => setSearchTerm('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Lists Grid */}
                <div className="flex-1 overflow-y-auto border dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800/50 min-h-0">
                  {isLoadingLists ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-500 dark:text-gray-400">Loading lists...</p>
                    </div>
                  ) : lists.length === 0 ? (
                    <div className="p-8 text-center">
                      <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">
                        {t("jobs.kanban.noListsAvailable", "No lists available")}
                      </h3>
                      <p className="text-gray-500">
                        {t("jobs.kanban.createListsFirst", "Create some lists first to organize your candidates")}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 p-4">
                      {filteredLists.map((list: any) => (
                        <div
                          key={list.id}
                          className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600"
                          onClick={() => handleListClick(list.id)}
                        >
                          {/* List Info */}
                          <div className="flex items-start space-x-3 mb-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400">
                                {list.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{list.name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Users className="h-3 w-3 text-gray-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">{list.candidate_count} candidates</span>
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          {list.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{list.description}</p>
                          )}

                          {/* Created Date */}
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              Created {format(new Date(list.created_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          /* Queries Tab */
          <AddFromQueryTab
            selectedCandidates={selectedCandidates}
            onCandidateToggle={handleCandidateToggle}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
        )}

        {/* Footer */}
        <div className="border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-4">
            {selectedCandidates.length > 0 && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t("jobs.kanban.selectedCount", "{{count}} selected", { count: selectedCandidates.length })}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={handleClose}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button 
              onClick={handleAddCandidates}
              disabled={selectedCandidates.length === 0 || isAdding}
              className="bg-blue-500 hover:bg-blue-500"
            >
              {isAdding ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {t("jobs.kanban.addCandidates", "Add {{count}} candidates", { count: selectedCandidates.length })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
